import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Sparkles, Loader2, Lightbulb, Copy, Check, AlertTriangle, ImageIcon as ImgIcon, Send,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type Aspect = '4:5' | '9:16';

type InspirationItem = {
  id: string;
  category: string;
  aspect: Aspect;
  imageUrl: string | null;
  caption: string;
  hashtags: string[];
  generatedFor: string;
  createdAt: string;
};

type CategoryRef = { key: string; label: string };

type InspirationResponse = {
  categories: CategoryRef[];
  posts: InspirationItem[];
};

type CloneResponse = {
  mediaId: string;
  caption: string;
  hashtags: string[];
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

export function Inspiration() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Inspiración · Zettapost';
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['inspiration'],
    queryFn: () => api<InspirationResponse>('/api/inspiration'),
    refetchInterval: 5 * 60_000,
  });

  const categories = data?.categories ?? [];
  const posts = data?.posts ?? [];

  const filtered = useMemo(
    () => (activeCategory === 'all' ? posts : posts.filter((p) => p.category === activeCategory)),
    [posts, activeCategory],
  );

  const cloneMutation = useMutation({
    mutationFn: (id: string) => api<CloneResponse>(`/api/inspiration/${id}/clone`, { method: 'POST' }),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo usar este post.'),
  });

  async function handleUse(item: InspirationItem) {
    setError(null);
    try {
      const result = await cloneMutation.mutateAsync(item.id);
      navigate('/dashboard/posts/new', {
        state: {
          prefillMediaId: result.mediaId,
          prefillCaption: result.caption,
          prefillHashtags: result.hashtags,
        },
      });
    } catch {
      /* error already set by onError */
    }
  }

  function copyCaption(item: InspirationItem) {
    const tags = item.hashtags.length ? '\n\n' + item.hashtags.map((t) => `#${t}`).join(' ') : '';
    void navigator.clipboard.writeText(item.caption + tags);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId((curr) => (curr === item.id ? null : curr)), 2000);
  }

  return (
    <div className="space-y-6">
      {/* header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Lightbulb size={22} className="text-brand-600" /> Inspiración
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Cada día nuestra IA genera un post nuevo por categoría — imagen + caption listos para publicar.
          Usa el que más encaje con tu marca y prográmalo en un clic.
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={15} /> {error}
        </p>
      )}

      {/* category chips */}
      <div className="flex flex-wrap gap-2">
        <CategoryChip
          label="Todas"
          active={activeCategory === 'all'}
          onClick={() => setActiveCategory('all')}
        />
        {categories.map((c) => (
          <CategoryChip
            key={c.key}
            label={c.label}
            active={activeCategory === c.key}
            onClick={() => setActiveCategory(c.key)}
          />
        ))}
      </div>

      {/* grid */}
      {isLoading ? (
        <div className="py-20 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Cargando inspiración…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          <Sparkles size={28} />
          <p className="text-sm">
            Aún no hay diseños{activeCategory !== 'all' ? ' en esta categoría' : ''}. El primer lote estará
            listo en breve — la IA está trabajando en ello.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {filtered.map((item) => {
            const cat = categories.find((c) => c.key === item.category);
            const aspectClass = item.aspect === '9:16' ? 'aspect-[9/16]' : 'aspect-[4/5]';
            return (
              <article
                key={item.id}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col"
              >
                <div className={cn('bg-slate-100 overflow-hidden relative', aspectClass)}>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={cat?.label ?? item.category}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-slate-300">
                      <ImgIcon size={32} />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-white/95 text-slate-700 rounded-full px-2 py-1 shadow-sm">
                    {item.aspect}
                  </span>
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-brand-600 uppercase tracking-wide truncate">
                      {cat?.label ?? item.category}
                    </span>
                    <span className="text-[11px] text-slate-400 shrink-0">
                      {fmtDate(item.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-line line-clamp-6">
                    {item.caption}
                  </p>
                  {item.hashtags.length > 0 && (
                    <p className="text-xs text-slate-400 line-clamp-2">
                      {item.hashtags.map((t) => `#${t}`).join(' ')}
                    </p>
                  )}
                  <div className="mt-auto pt-2 flex items-center gap-2">
                    <button
                      onClick={() => handleUse(item)}
                      disabled={cloneMutation.isPending}
                      className="btn-primary gap-1.5 flex-1 disabled:opacity-60"
                    >
                      {cloneMutation.isPending && cloneMutation.variables === item.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Usar este post
                    </button>
                    <button
                      onClick={() => copyCaption(item)}
                      title="Copiar caption"
                      className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-600 transition shrink-0"
                    >
                      {copiedId === item.id ? (
                        <Check size={15} className="text-emerald-600" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'text-sm font-medium rounded-full px-3.5 py-1.5 border transition',
        active
          ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900',
      )}
    >
      {label}
    </button>
  );
}
